import React, { useState } from "react"

import { Building, Mail, MessagesSquare, Phone } from "lucide-react"
import { useForm } from "react-hook-form"

import sendEmail from "@utils/sendEmail"
import Popup from "../modal"

const strings = {
  fr: {
    meet: "Nous rencontrer",
    meetTitle: "21 Rue de Froeschwiller 67110 Reichshoffen, France",
    phoneTitle: "Nous téléphoner",
    rawPhone: "+33388808630",
    chatTitle: "Chattez",
    chat: "Grâce au module de chat situé en bas à droite de cette page",
    emailTitle: "Par mail",
    emailFehr: "info@fehrgroup.com",
    name: "Prénom",
    lastname: "Nom",
    email: "Email",
    phone: "Téléphone",
    company: "Société",
    adress: "Adresse",
    zip: "Code postal",
    town: "Ville",
    activity: "Secteur d'activité",
    sectors: [
      "Agroalimentaire",
      "Construction (BTP et génie civil)",
      "Architecte",
      "Expédition et logistique",
      "Paysagiste",
      "Collectivité locale",
      "Particulier",
      "Autre",
    ],
    message: "Message",
    submit: "Envoyer",
    success_alert_title: "Message envoyé !",
    success_alert_body: "Nous allons vous répondre dans les meilleurs délais.",
    close_button: "Fermer",
    form_state: "Message en cours d'envoi ...",
  },
  de: {
    meet: "Besuchen Sie uns",
    // meetTitle:
    //   "Fehr Technologies GmbH & Co KG Triebstraße 34 D-68753 WAGHÄUSEL – WIESENTAL",
    // phoneTitle: "Telefon",
    // rawPhone: "+4972542090",
    chatTitle: "Chatte mit uns",
    chat: "Dank des Chat-Moduls unten rechts auf dieser Seite",
    emailTitle: "Email",
    emailFehr: "info@fehrgroup.com",
    name: "Vorname",
    lastname: "Name",
    email: "Email",
    phone: "Telefon",
    company: "Firma",
    adress: "Adresse",
    zip: "Postleitzahl",
    town: "Stadt",
    // activity: "Tätigkeitsbereich",
    // sectors: [
    //   "Agroalimentaire",
    //   "Construction (BTP et génie civil)",
    //   "Architecte",
    //   "Expédition et logistique",
    //   "Paysagiste",
    //   "Collectivité locale",
    //   "Particulier",
    //   "Autre",
    // ],
    message: "Message",
    submit: "Senden",
    success_alert_title: "Nachricht gesendet!",
    success_alert_body: "Vielen Dank für Ihre E-Mail",
    close_button: "Schließen",
    form_state: "Nachricht wird gesendet ...",
  },
  en: {
    meet: "Come and see us",
    meetTitle: "21 Rue de Froeschwiller 67110 Reichshoffen, France",
    phoneTitle: "Phone",
    rawPhone: "+33388808630",
    chatTitle: "Chat with us",
    chat: "Use the chat module at the bottom right of this page",
    emailTitle: "Email",
    emailFehr: "info@fehrgroup.com",
    name: "First name",
    lastname: "Last name",
    email: "Email",
    phone: "Phone",
    company: "Company",
    adress: "Adress",
    zip: "ZIP code",
    town: "Town",
    activity: "Field",
    sectors: [
      "Food",
      "Construction (public works and civil engineering)",
      "Architect",
      "Shipping and logistics",
      "Landscaper",
      "Local community",
      "Particular",
      "Other",
    ],
    message: "Message",
    submit: "Send",
    success_alert_title: "Message sent!",
    success_alert_body:
      "Thank you for your message. We'll get back to you shortly.",
    close_button: "Close",
    form_state: "Sending message ...",
  },
}

export default function ContactForm(props) {
  const [modalOpen, setModalOpen] = useState(false)
  const { register, handleSubmit } = useForm()

  // Create an event handler so you can call the verification on button click event or form submit
  const sendMail = async (data, e) => {
    document.getElementById("form-state").innerHTML =
      strings[props.lang].form_state

    sendEmail(data)
      .then((resolve) => {
        setModalOpen(true)
        document.getElementById("form-state").innerHTML = ""
      })
      .catch((err) => {
        alert(err)
        document.getElementById("form-state").innerHTML = ""
      })
    e.target.reset()
  }

  return (
    <div className="relative isolate bg-white">
      <div className="mx-auto grid max-w-7xl grid-cols-1 lg:grid-cols-2">
        <div className="relative px-6 pb-20 pt-24 sm:pt-24 lg:static lg:px-8">
          <div className="mx-auto max-w-xl lg:mx-0 lg:max-w-lg">
            <div className="absolute inset-y-0 left-0 -z-10 w-full overflow-hidden bg-gray-100 ring-1 ring-gray-900/10 lg:w-1/2">
              <svg
                className="absolute inset-0 h-full w-full stroke-gray-200 [mask-image:radial-gradient(100%_100%_at_top_right,white,transparent)]"
                aria-hidden="true"
              >
                <defs>
                  <pattern
                    id="83fd4e5a-9d52-42fc-97b6-718e5d7ee527"
                    width={200}
                    height={200}
                    x="100%"
                    y={-1}
                    patternUnits="userSpaceOnUse"
                  >
                    <path d="M130 200V.5M.5 .5H200" fill="none" />
                  </pattern>
                </defs>
                <rect width="100%" height="100%" strokeWidth={0} fill="white" />
                <svg x="100%" y={-1} className="overflow-visible fill-gray-50">
                  <path d="M-470.5 0h201v201h-201Z" strokeWidth={0} />
                </svg>
                <rect
                  width="100%"
                  height="100%"
                  strokeWidth={0}
                  fill="url(#83fd4e5a-9d52-42fc-97b6-718e5d7ee527)"
                />
              </svg>
            </div>
            {props.children}
            <h2 className="text-3xl font-bold tracking-tight text-gray-900">
              {strings[props.lang].meet}
            </h2>
            <dl className="mt-10 space-y-4 text-base leading-7 text-gray-600">
              {strings[props.lang].meetTitle && (
                <div className="flex gap-x-4">
                  <dt className="flex-none">
                    <span className="sr-only">Address</span>
                    <Building
                      className="h-7 w-6 text-gray-400"
                      aria-hidden="true"
                    />
                  </dt>
                  <dd>{strings[props.lang].meetTitle}</dd>
                </div>
              )}
              {strings[props.lang].rawPhone && (
                <div className="flex gap-x-4">
                  <dt className="flex-none">
                    <span className="sr-only">Telephone</span>
                    <Phone
                      className="h-7 w-6 text-gray-400"
                      aria-hidden="true"
                    />
                  </dt>
                  <dd>
                    <a
                      className="hover:text-gray-900"
                      href="tel:+1 (555) 234-5678"
                    >
                      {strings[props.lang].rawPhone}
                    </a>
                  </dd>
                </div>
              )}
              <div className="flex gap-x-4">
                <dt className="flex-none">
                  <span className="sr-only">Email</span>
                  <Mail className="h-7 w-6 text-gray-400" aria-hidden="true" />
                </dt>
                <dd>
                  <a
                    className="hover:text-gray-900"
                    href={`mailto:${strings[props.lang].emailFehr}`}
                  >
                    {strings[props.lang].emailFehr}
                  </a>
                </dd>
              </div>
              <div className="flex gap-x-4">
                <dt className="flex-none">
                  <span className="sr-only">Address</span>
                  <MessagesSquare
                    className="h-7 w-6 text-gray-400"
                    aria-hidden="true"
                  />
                </dt>
                <dd>
                  {strings[props.lang].chatTitle} {" : "}
                  <span className="lowercase">{strings[props.lang].chat}</span>
                </dd>
              </div>
            </dl>
          </div>
        </div>
        <form
          onSubmit={handleSubmit(sendMail)}
          className="px-6 pb-24 pt-2 sm:pb-32 lg:px-8"
        >
          <div className="mx-auto max-w-xl lg:mr-0 lg:max-w-lg">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <div>
                <label
                  htmlFor="firstname"
                  className="block text-sm font-semibold leading-6 text-gray-900"
                >
                  {strings[props.lang].name}
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    name="firstname"
                    id="firstname"
                    autoComplete="given-name"
                    className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-fehr-500 sm:text-sm sm:leading-6"
                    {...register("firstname", {
                      required: true,
                      maxLength: 80,
                    })}
                  />
                </div>
              </div>
              <div>
                <label
                  htmlFor="lastname"
                  className="block text-sm font-semibold leading-6 text-gray-900"
                >
                  {strings[props.lang].lastname}
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    name="lastname"
                    id="lastname"
                    autoComplete="family-name"
                    className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-fehr-500 sm:text-sm sm:leading-6"
                    {...register("lastname", {
                      required: true,
                      maxLength: 100,
                    })}
                  />
                </div>
              </div>
              <div className="sm:col-span-2">
                <label
                  htmlFor="email"
                  className="block text-sm font-semibold leading-6 text-gray-900"
                >
                  {strings[props.lang].email}
                </label>
                <div className="mt-1">
                  <input
                    type="email"
                    name="email"
                    id="email"
                    autoComplete="email"
                    className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-fehr-500 sm:text-sm sm:leading-6"
                    {...register("email", {
                      required: true,
                      pattern: /^\S+@\S+$/i,
                    })}
                  />
                </div>
              </div>
              <div className="sm:col-span-2">
                <label
                  htmlFor="phone"
                  className="block text-sm font-semibold leading-6 text-gray-900"
                >
                  {strings[props.lang].phone}
                </label>
                <div className="mt-1">
                  <input
                    type="tel"
                    name="phone"
                    id="phone"
                    autoComplete="tel"
                    className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-fehr-500 sm:text-sm sm:leading-6"
                    {...register("phone", { required: true, maxLength: 20 })}
                  />
                </div>
              </div>
              <div className="sm:col-span-2">
                <label
                  htmlFor="company"
                  className="block text-sm font-semibold leading-6 text-gray-900"
                >
                  {strings[props.lang].company}
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    name="company"
                    id="company"
                    className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-fehr-500 sm:text-sm sm:leading-6"
                    {...register("company")}
                  />
                </div>
              </div>
              <div className="sm:col-span-2">
                <label
                  htmlFor="adress"
                  className="block text-sm font-semibold leading-6 text-gray-900"
                >
                  {strings[props.lang].adress}
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    name="adress"
                    id="adress"
                    className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-fehr-500 sm:text-sm sm:leading-6"
                    {...register("address")}
                  />
                </div>
              </div>
              <div>
                <label
                  htmlFor="zip"
                  className="block text-sm font-semibold leading-6 text-gray-900"
                >
                  {strings[props.lang].zip}
                </label>
                <div className="mt-1">
                  <input
                    type="number"
                    name="zip"
                    id="zip"
                    className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-fehr-500 sm:text-sm sm:leading-6"
                    {...register("zip")}
                  />
                </div>
              </div>
              <div>
                <label
                  htmlFor="town"
                  className="block text-sm font-semibold leading-6 text-gray-900"
                >
                  {strings[props.lang].town}
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    name="town"
                    id="town"
                    className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-fehr-500 sm:text-sm sm:leading-6"
                    {...register("town")}
                  />
                </div>
              </div>
              {strings[props.lang].activity && (
                <div className="sm:col-span-2">
                  <label
                    htmlFor="sector"
                    className="block text-sm font-semibold leading-6 text-gray-900"
                  >
                    {strings[props.lang].activity}
                  </label>
                  <div className="mt-1">
                    <select
                      {...register("sector")}
                      className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-fehr-500 sm:text-sm sm:leading-6"
                    >
                      {strings[props.lang].sectors.map((sector, i) => {
                        return <option key={i}>{sector}</option>
                      })}
                    </select>
                  </div>
                </div>
              )}
              <div className="sm:col-span-2">
                <label
                  htmlFor="message"
                  className="block text-sm font-semibold leading-6 text-gray-900"
                >
                  {strings[props.lang].message}
                </label>
                <div className="mt-1">
                  <textarea
                    name="message"
                    id="message"
                    rows={4}
                    className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-fehr-500 sm:text-sm sm:leading-6"
                    defaultValue={""}
                    {...register("message", { required: true })}
                  />
                </div>
              </div>
            </div>
            <div className="mt-8 flex justify-end">
              <p className="italic" id="form-state"></p>
              <button
                type="submit"
                className="rounded-sm bg-fehr-500 px-3.5 py-2.5 text-center text-base font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              >
                {strings[props.lang].submit}
              </button>
            </div>
          </div>
        </form>
        <div id="modal"></div>
        <Popup
          show={modalOpen}
          title={strings[props.lang].success_alert_title}
          body={strings[props.lang].success_alert_body}
          close_button={strings[props.lang].close_button}
          onClose={() => setModalOpen(false)}
        />
      </div>
    </div>
  )
}
